// Frontend/geniac/src/contexts/CurrencyContext.tsx
// Context pour gérer la devise sélectionnée dans toute l'application

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Currency = 'EUR' | 'USD' | 'GBP' | 'CHF';

export interface CurrencyConfig {
  code: Currency;
  symbol: string;
  name: string;
  locale: string;
  countries: string[];
}

export interface PlanPrice {
  plan: string;
  currency: Currency;
  monthly: number;
  yearly: number;
  symbol: string;
  locale: string;
}

export interface AllPrices {
  currency: Currency;
  symbol: string;
  locale: string;
  plans: {
    free: PlanPrice;
    standard: PlanPrice;
    premium: PlanPrice;
  };
}

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  formatAmount: (amount: number, currencyCode?: Currency) => string;
  currencySymbol: string;
  isLoading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

const STORAGE_KEY = 'geniac_preferred_currency';
const DEFAULT_CURRENCY: Currency = 'EUR';

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  CHF: 'CHF'
};

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [currency, setCurrencyState] = useState<Currency>(DEFAULT_CURRENCY);
  const [isLoading, setIsLoading] = useState(true);

  // Charger la devise depuis localStorage au montage
  useEffect(() => {
    const loadCurrency = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored && ['EUR', 'USD', 'GBP', 'CHF'].includes(stored)) {
          setCurrencyState(stored as Currency);
        } else {
          // Détecter la devise du navigateur
          const browserLocale = navigator.language;
          if (browserLocale.includes('en-US')) {
            setCurrencyState('USD');
          } else if (browserLocale.includes('en-GB')) {
            setCurrencyState('GBP');
          } else if (browserLocale.includes('de-CH') || browserLocale.includes('fr-CH') || browserLocale.includes('it-CH')) {
            setCurrencyState('CHF');
          } else {
            setCurrencyState('EUR'); // Par défaut
          }
        }
      } catch (error) {
        console.error('Error loading currency from localStorage:', error);
        setCurrencyState(DEFAULT_CURRENCY);
      } finally {
        setIsLoading(false);
      }
    };

    loadCurrency();
  }, []);

  // Sauvegarder la devise dans localStorage quand elle change
  const setCurrency = (newCurrency: Currency) => {
    try {
      setCurrencyState(newCurrency);
      localStorage.setItem(STORAGE_KEY, newCurrency);
    } catch (error) {
      console.error('Error saving currency to localStorage:', error);
    }
  };

  // Formater un montant selon la devise
  const formatAmount = (amount: number, currencyCode: Currency = currency): string => {
    const symbol = CURRENCY_SYMBOLS[currencyCode];

    // Utiliser le formatage natif selon la locale
    try {
      let locale = 'fr-FR';
      switch (currencyCode) {
        case 'USD':
          locale = 'en-US';
          break;
        case 'GBP':
          locale = 'en-GB';
          break;
        case 'CHF':
          locale = 'de-CH';
          break;
        default:
          locale = 'fr-FR';
      }

      const formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);

      // Symbole avant ou après selon la devise
      if (currencyCode === 'EUR') {
        return `${formatted} ${symbol}`;
      } else if (currencyCode === 'CHF') {
        return `${symbol} ${formatted}`;
      } else {
        return `${symbol}${formatted}`;
      }
    } catch (error) {
      console.error('Error formatting amount:', error);
      return `${symbol} ${amount.toFixed(2)}`;
    }
  };

  const value: CurrencyContextType = {
    currency,
    setCurrency,
    formatAmount,
    currencySymbol: CURRENCY_SYMBOLS[currency],
    isLoading
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

// Hook pour utiliser le context
export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
